import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, AlertTriangle, AlertOctagon, CheckCircle2, RefreshCw, ChevronDown, ChevronUp, ArrowUpDown, Database, ExternalLink } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";

// Build the cluster-detail URL from a threat-summary drill-down row.
// Handles the truncated fingerprint previews the backend returns
// ("p2021xRTHj…") — the /cluster/:kind/:key endpoint strips the ellipsis
// and matches by prefix regex on the fingerprint field.
const buildClusterHref = (kind, row) => {
  if (!row) return `/admin/fraud/cluster/${kind}`;
  if (kind === "card_fp") return `/admin/fraud/cluster/card_fp/${encodeURIComponent(row.card_fp || "")}`;
  if (kind === "device_fp") return `/admin/fraud/cluster/device_fp/${encodeURIComponent(row.device_fp || "")}`;
  if (kind === "ip_range") return `/admin/fraud/cluster/ip_range/${encodeURIComponent(row.ip || "")}`;
  if (kind === "recurring_buyer") return `/admin/fraud/cluster/recurring_buyer/${encodeURIComponent(`${row.buyer_user_id || ""}:${row.affiliate_user_id || ""}`)}`;
  return `/admin/fraud/cluster/${kind}`;
};

/**
 * Extended threat summary card for the Guardian tab.
 *
 * Renders the same overall CODE the Dashboard tile shows, but with the
 * full signal breakdown (case tiers, fingerprint clusters, IP-range
 * clusters, recurring buyer pairs, high-value $ on hold, and confirmed
 * fraud counts).
 */
export default function GuardianThreatSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPanel, setOpenPanel] = useState(null); // card_fp | device_fp | ip_range | recurring_buyer
  const [backfilling, setBackfilling] = useState(false);

  const load = () => {
    setLoading(true);
    authAxios.get("/admin/fraud/threat-summary")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    if (backfilling) return;
    if (!window.confirm(
      "Retrieve every PaymentIntent from Stripe in the last 90 days that has no card fingerprint on file, "
      + "and stamp fingerprint / last4 / brand onto the payment_transactions row?\n\n"
      + "Idempotent — rows already stamped are skipped. Bounded to 500 rows per run."
    )) return;
    setBackfilling(true);
    try {
      const r = await authAxios.post("/admin/fraud/backfill-card-fingerprints?days=90&limit=500");
      const d = r.data || {};
      toast.success(
        `Backfill done — scanned ${d.scanned}, stamped ${d.stamped}, `
        + `skipped ${d.skipped_no_charge}, errors ${d.error_count}`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !data) {
    return (
      <div
        className="rounded-xl border border-white/[0.06] bg-[#0a0a0f] p-6 h-40 animate-pulse"
        data-testid="guardian-threat-summary-loading"
      />
    );
  }
  if (!data) return null;

  const style = STYLES[data.code] || STYLES.GREEN;
  const Icon = style.icon;
  const flashing = data.code === "HIGH_ALERT";
  const s = data.signals || {};

  return (
    <div
      data-testid="guardian-threat-summary"
      className={`
        relative overflow-hidden rounded-xl border p-6 transition-all
        ${style.card}
        ${flashing ? "animate-guardian-flash-extended" : ""}
      `}
    >
      {flashing && (
        <style>{`
          @keyframes guardian-flash-extended {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); border-color: rgba(239,68,68,0.9); }
            50%      { box-shadow: 0 0 30px 6px rgba(239,68,68,0.75); border-color: rgba(255,90,90,1); }
          }
          .animate-guardian-flash-extended { animation: guardian-flash-extended 1.2s ease-in-out infinite; }
        `}</style>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className={`w-6 h-6 flex-shrink-0 ${style.iconTone}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider">
                Threat Summary
              </h3>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${style.badge}`}
                data-testid="threat-summary-code"
              >
                {data.code === "HIGH_ALERT" ? "High Alert" : data.code}
              </span>
            </div>
            <p className={`mt-1 text-sm ${style.headline}`} data-testid="threat-summary-headline">
              {data.headline}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded text-white/40 hover:text-white/80 hover:bg-white/5"
          title="Refresh threat summary"
          data-testid="threat-summary-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
        <button
          onClick={backfill}
          disabled={backfilling}
          className="ml-1 flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider text-white/40 hover:text-white/80 hover:bg-white/5 border border-white/10"
          title="Backfill card fingerprints from Stripe (last 90 days, up to 500 rows). Idempotent."
          data-testid="threat-summary-backfill-cards"
        >
          <Database className={`w-3 h-3 ${backfilling ? "animate-pulse" : ""}`} />
          {backfilling ? "Working…" : "Backfill Cards"}
        </button>
      </div>

      {/* Signal grid */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Freeze" value={s.open_freeze} tone={s.open_freeze ? "red" : "muted"} testid="ts-freeze" />
        <Stat label="Prewarn" value={s.open_prewarn} tone={s.open_prewarn ? "amber" : "muted"} testid="ts-prewarn" />
        <Stat label="Watch" value={s.open_watch} tone={s.open_watch ? "white" : "muted"} testid="ts-watch" />
        <Stat label="HV Hold" value={s.open_high_value} sub={s.high_value_hold_cents ? `$${(s.high_value_hold_cents / 100).toLocaleString()}` : null} tone={s.open_high_value ? "cyan" : "muted"} testid="ts-hv" />
        <Stat label="Confirmed 7d" value={s.confirmed_fraud_7d} tone={s.confirmed_fraud_7d ? "red" : "muted"} testid="ts-confirmed7" />
        <Stat label="Confirmed 30d" value={s.confirmed_fraud_30d} tone={s.confirmed_fraud_30d ? "red" : "muted"} testid="ts-confirmed30" />
      </div>

      {/* Pattern signals (highlighted separately — these drive HIGH_ALERT) */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <PatternStat
          label="Duplicate card fingerprints"
          value={s.duplicate_fp_clusters}
          detail={s.duplicate_fp_users ? `${s.duplicate_fp_users} users · ${s.duplicate_fp_clusters} cluster${s.duplicate_fp_clusters !== 1 ? "s" : ""}` : "—"}
          hot={s.duplicate_fp_clusters >= 2}
          open={openPanel === "card_fp"}
          onToggle={() => setOpenPanel(openPanel === "card_fp" ? null : "card_fp")}
          testid="ts-fp"
        />
        <PatternStat
          label="Shared device fingerprints"
          value={s.device_fp_clusters}
          detail={s.device_fp_users ? `${s.device_fp_users} users · ${s.device_fp_clusters} cluster${s.device_fp_clusters !== 1 ? "s" : ""}` : "—"}
          hot={s.device_fp_clusters >= 2}
          open={openPanel === "device_fp"}
          onToggle={() => setOpenPanel(openPanel === "device_fp" ? null : "device_fp")}
          testid="ts-device-fp"
        />
        <PatternStat
          label="IP-range clusters (≥3 signups)"
          value={s.ip_range_clusters}
          detail={s.ip_range_users ? `${s.ip_range_users} signups · ${s.ip_range_clusters} IP${s.ip_range_clusters !== 1 ? "s" : ""}` : "—"}
          hot={s.ip_range_clusters >= 2}
          open={openPanel === "ip_range"}
          onToggle={() => setOpenPanel(openPanel === "ip_range" ? null : "ip_range")}
          testid="ts-ip"
        />
        <PatternStat
          label="Recurring buyer patterns"
          value={s.recurring_buyer_pairs}
          detail={s.recurring_buyer_pairs ? `${s.recurring_buyer_pairs} buyer→affiliate pair${s.recurring_buyer_pairs !== 1 ? "s" : ""}` : "—"}
          hot={s.recurring_buyer_pairs >= 3}
          open={openPanel === "recurring_buyer"}
          onToggle={() => setOpenPanel(openPanel === "recurring_buyer" ? null : "recurring_buyer")}
          testid="ts-recurring"
        />
      </div>

      {/* Expanded drill-down table for the tapped pattern stat */}
      {openPanel && (
        <div className="mt-4" data-testid={`ts-panel-${openPanel}`}>
          <ClusterTable kind={openPanel} rows={(data.clusters || {})[openPanel] || []} />
        </div>
      )}

      {(data.reasons || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/5">
          <div className="text-white/40 text-[10px] uppercase tracking-wider mb-2">Why this code</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="threat-summary-reasons">
            {data.reasons.map((r, i) => (
              <li key={i} className="text-xs text-white/70 flex gap-2">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                <span className="min-w-0">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[10px] text-white/25 font-mono">
        Computed {data.computed_at ? new Date(data.computed_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone, testid }) {
  const toneClass = {
    red: "text-red-400",
    amber: "text-amber-300",
    cyan: "text-cyan-300",
    white: "text-white/90",
    muted: "text-white/30",
  }[tone || "muted"];
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/[0.04] p-3" data-testid={testid}>
      <div className="text-white/40 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`mt-1 text-lg font-mono ${toneClass}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-white/40 font-mono">{sub}</div>}
    </div>
  );
}

function PatternStat({ label, value, detail, hot, open, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        hot ? "bg-red-500/[0.06] border-red-500/25 hover:border-red-500/50"
            : "bg-white/[0.02] border-white/[0.04] hover:border-white/15"
      } ${open ? "ring-1 ring-white/20" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <div className={`text-[10px] uppercase tracking-wider ${hot ? "text-red-300" : "text-white/40"}`}>{label}</div>
        {open
          ? <ChevronUp className="w-3 h-3 text-white/40" />
          : <ChevronDown className="w-3 h-3 text-white/30" />}
      </div>
      <div className={`mt-1 text-lg font-mono ${hot ? "text-red-300" : value ? "text-white/85" : "text-white/30"}`}>
        {value ?? 0}
      </div>
      <div className="text-[10px] text-white/40">{detail}</div>
    </button>
  );
}

// ─── Drill-down table (sortable) ────────────────────────────────
function ClusterTable({ kind, rows }) {
  const cols = COLUMN_SPEC[kind];
  const [sortBy, setSortBy] = useState(cols[0].key);
  const [sortDir, setSortDir] = useState("desc");

  if (!rows.length) {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-black/30 p-4 text-xs text-white/40 text-center" data-testid={`cluster-table-empty-${kind}`}>
        No {LABELS[kind].toLowerCase()} match the current thresholds.
      </div>
    );
  }

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortBy] ?? "";
    const bv = b[sortBy] ?? "";
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }
    return sortDir === "asc"
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const setSort = (key) => {
    if (key === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("desc"); }
  };

  return (
    <div className="rounded-lg border border-white/[0.06] bg-black/30 overflow-hidden" data-testid={`cluster-table-${kind}`}>
      <div className="px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-wider text-white/50 flex items-center justify-between">
        <span>{LABELS[kind]} — top {sorted.length}</span>
        <span className="text-white/30">Sort: <span className="text-white/60">{cols.find((c) => c.key === sortBy)?.label}</span> {sortDir === "asc" ? "↑" : "↓"}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-white/40 uppercase tracking-wider text-[10px]">
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`py-2 px-3 select-none ${c.align === "right" ? "text-right" : "text-left"}`}
                >
                  <button
                    type="button"
                    onClick={() => setSort(c.key)}
                    className={`inline-flex items-center gap-1 hover:text-white/80 ${sortBy === c.key ? "text-white/85" : ""}`}
                    data-testid={`sort-${kind}-${c.key}`}
                  >
                    {c.label}
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </button>
                </th>
              ))}
              <th className="py-2 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr key={i} className="border-t border-white/[0.04] text-white/80">
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={`py-2 px-3 ${c.mono ? "font-mono" : ""} ${c.align === "right" ? "text-right" : ""}`}
                  >
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                  </td>
                ))}
                <td className="py-2 px-3 text-right">
                  <Link
                    to={buildClusterHref(kind, row)}
                    data-testid={`cluster-view-${kind}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/15 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5"
                  >
                    View <ExternalLink className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LABELS = {
  card_fp: "Duplicate card fingerprints",
  device_fp: "Shared device fingerprints",
  ip_range: "IP-range clusters",
  recurring_buyer: "Recurring buyer patterns",
};

const COLUMN_SPEC = {
  card_fp: [
    { key: "n_users", label: "Users", align: "right", mono: true },
    { key: "n_txns", label: "Txns", align: "right", mono: true },
    { key: "card_fp", label: "Fingerprint", mono: true },
    { key: "user_ids", label: "User IDs (sample)", mono: true,
      render: (v) => (v || []).slice(0, 4).join(", ") + ((v || []).length > 4 ? " …" : "") },
  ],
  device_fp: [
    { key: "n_users", label: "Users", align: "right", mono: true },
    { key: "device_fp", label: "Device FP", mono: true },
    { key: "last_seen", label: "Last seen", mono: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : "—" },
    { key: "user_ids", label: "User IDs (sample)", mono: true,
      render: (v) => (v || []).slice(0, 4).join(", ") + ((v || []).length > 4 ? " …" : "") },
  ],
  ip_range: [
    { key: "n_signups", label: "Signups", align: "right", mono: true },
    { key: "ip", label: "IP", mono: true },
    { key: "user_ids", label: "User IDs (sample)", mono: true,
      render: (v) => (v || []).slice(0, 4).join(", ") + ((v || []).length > 4 ? " …" : "") },
  ],
  recurring_buyer: [
    { key: "n_purchases", label: "Purchases", align: "right", mono: true },
    { key: "total_cents", label: "Total", align: "right", mono: true,
      render: (v) => `$${((v || 0) / 100).toFixed(2)}` },
    { key: "buyer_user_id", label: "Buyer", mono: true },
    { key: "affiliate_user_id", label: "Affiliate", mono: true },
  ],
};

const STYLES = {
  GREEN: {
    icon: CheckCircle2,
    iconTone: "text-emerald-400",
    card: "bg-emerald-500/[0.04] border-emerald-500/20",
    badge: "bg-emerald-500/15 text-emerald-300",
    headline: "text-emerald-200",
    dot: "bg-emerald-400",
  },
  AMBER: {
    icon: AlertTriangle,
    iconTone: "text-amber-300",
    card: "bg-amber-500/[0.05] border-amber-500/25",
    badge: "bg-amber-500/15 text-amber-300",
    headline: "text-amber-100",
    dot: "bg-amber-400",
  },
  RED: {
    icon: AlertOctagon,
    iconTone: "text-red-400",
    card: "bg-red-500/[0.06] border-red-500/30",
    badge: "bg-red-500/20 text-red-300",
    headline: "text-red-100",
    dot: "bg-red-400",
  },
  HIGH_ALERT: {
    icon: AlertOctagon,
    iconTone: "text-red-300",
    card: "bg-red-500/[0.10] border-red-500/60 shadow-[0_0_18px_-4px_rgba(239,68,68,0.55)]",
    badge: "bg-red-600/40 text-red-100",
    headline: "text-red-50",
    dot: "bg-red-300",
  },
};
